/**
 * Списък на разговорите с филтри и метрики (Раздел 8).
 */

import { useMemo, useState } from 'react';
import {
  Badge,
  Card,
  Group,
  Pagination,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { DatePickerInput, TimePicker } from '@mantine/dates';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../api/client';
import { appendPeriod, appendTimeRange, timeRangeError } from '../lib/period';
import type {
  DashboardStats,
  ExtensionItem,
  RecordingListItem,
  RecordingListResponse,
} from '../api/types';

const PAGE_SIZE = 50;

/**
 * Mantine дава на всяко поле за цифри точно `2ch` — ширината на две нули.
 *
 * Шрифт, чиито цифри са дори малко по-широки от „0“, остава без място и
 * втората цифра се отрязва: при Comfortaa недостигът е 1,4px на 14px шрифт.
 * Запасът не зависи от мерките на конкретния шрифт.
 */
const TIME_FIELD_STYLES = { field: { width: 'calc(2ch + 0.6em)' } };

const STATUS_LABELS: Record<string, string> = {
  QUEUED: 'В опашка',
  PROCESSING: 'Обработва се',
  COMPLETED: 'Готов',
  FAILED: 'Грешка',
};

export function RecordingsPage() {
  const navigate = useNavigate();
  // Компонентът връща „ГГГГ-ММ-ДД“ като низ, не Date.
  const [period, setPeriod] = useState<[string | null, string | null]>([null, null]);
  // Часът е отделен от датата: прилага се за всеки ден от избрания период.
  const [hours, setHours] = useState<[string | null, string | null]>([null, null]);
  const [extensionId, setExtensionId] = useState<string | null>(null);
  const [direction, setDirection] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [flaggedOnly, setFlaggedOnly] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const hoursError = timeRangeError(hours);

  const query = useMemo(() => {
    const params = appendTimeRange(appendPeriod(new URLSearchParams(), period), hours);
    if (extensionId) params.set('extensionId', extensionId);
    if (direction) params.set('direction', direction);
    if (status) params.set('status', status);
    if (flaggedOnly === 'true') params.set('flaggedOnly', 'true');
    params.set('page', String(page));
    params.set('pageSize', String(PAGE_SIZE));
    return params.toString();
  }, [period, hours, extensionId, direction, status, flaggedOnly, page]);

  const recordings = useQuery({
    queryKey: ['recordings', query],
    queryFn: () => apiRequest<RecordingListResponse>(`/api/recordings?${query}`),
    // При обърнат диапазон заявката няма смисъл — филтърът показва причината.
    enabled: hoursError === null,
    // Записите в обработка се обновяват сами, без ръчно презареждане.
    refetchInterval: 15_000,
  });

  const statsQuery = useMemo(
    () =>
      appendTimeRange(appendPeriod(new URLSearchParams(), period), hours).toString(),
    [period, hours],
  );

  const stats = useQuery({
    queryKey: ['recordings-stats', statsQuery],
    queryFn: () => apiRequest<DashboardStats>(`/api/recordings/stats?${statsQuery}`),
    enabled: hoursError === null,
    refetchInterval: 15_000,
  });

  const extensions = useQuery({
    queryKey: ['extensions'],
    queryFn: () => apiRequest<{ items: ExtensionItem[] }>('/api/extensions'),
    staleTime: 5 * 60_000,
  });

  const totalPages = Math.max(1, Math.ceil((recordings.data?.total ?? 0) / PAGE_SIZE));

  return (
    <Stack>
      <Title order={3}>Разговори</Title>

      <SimpleGrid cols={{ base: 2, md: 4 }}>
        <StatCard label="Обработени" value={stats.data?.completed ?? '—'} />
        <StatCard label="В обработка" value={stats.data?.inProgress ?? '—'} />
        <StatCard label="С грешка" value={stats.data?.failed ?? '—'} color="red" />
        <StatCard
          label="Средна оценка"
          value={stats.data?.averageScore ?? '—'}
          hint={
            stats.data?.flaggedShare !== null && stats.data?.flaggedShare !== undefined
              ? `${stats.data.flaggedShare}% проблемни`
              : undefined
          }
        />
      </SimpleGrid>

      <Card withBorder>
        <Group align="end">
          <DatePickerInput
            type="range"
            label="Период"
            value={period}
            onChange={(value) => {
              setPeriod(value as [string | null, string | null]);
              setPage(1);
            }}
            clearable
            // Без това избирането на един и същи ден два пъти изчиства периода,
            // а един ден е най-честият избор.
            allowSingleDateInRange
            w={240}
            valueFormat="DD.MM.YYYY"
          />
          <TimePicker
            label="Час от"
            value={hours[0] ?? ''}
            onChange={(value) => {
              setHours([value || null, hours[1]]);
              setPage(1);
            }}
            error={hoursError !== null}
            clearable
            styles={TIME_FIELD_STYLES}
            w={150}
          />
          <TimePicker
            label="Час до"
            value={hours[1] ?? ''}
            onChange={(value) => {
              setHours([hours[0], value || null]);
              setPage(1);
            }}
            error={hoursError !== null}
            clearable
            styles={TIME_FIELD_STYLES}
            w={150}
          />
          <Select
            label="Служител"
            placeholder="Всички"
            data={(extensions.data?.items ?? []).map((item) => ({
              value: item.id,
              label: `${item.employeeName} (вътр. ${item.extension})`,
            }))}
            value={extensionId}
            onChange={(value) => {
              setExtensionId(value);
              setPage(1);
            }}
            clearable
            searchable
            w={240}
          />
          <Select
            label="Посока"
            placeholder="Всички"
            data={[
              { value: 'IN', label: 'Входящо' },
              { value: 'OUT', label: 'Изходящо' },
            ]}
            value={direction}
            onChange={(value) => {
              setDirection(value);
              setPage(1);
            }}
            clearable
            w={150}
          />
          <Select
            label="Статус"
            placeholder="Всички"
            data={Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }))}
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
            clearable
            w={170}
          />
          <Select
            label="Проблемни"
            placeholder="Всички"
            data={[{ value: 'true', label: 'Само проблемни' }]}
            value={flaggedOnly}
            onChange={(value) => {
              setFlaggedOnly(value);
              setPage(1);
            }}
            clearable
            w={170}
          />
        </Group>

        {hoursError !== null && (
          <Text c="red" size="sm" mt="xs">
            {hoursError}
          </Text>
        )}
      </Card>

      <Card withBorder p={0}>
        <Table highlightOnHover striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Дата и час</Table.Th>
              <Table.Th>Служител</Table.Th>
              <Table.Th>Група</Table.Th>
              <Table.Th>Посока</Table.Th>
              <Table.Th>Времетраене</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th>Оценка</Table.Th>
              <Table.Th>Проблемен</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(recordings.data?.items ?? []).map((item) => (
              <Table.Tr
                key={item.id}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/recordings/${item.id}`)}
              >
                <Table.Td>{formatDateTime(item.callStartedAt)}</Table.Td>
                <Table.Td>
                  {item.employeeName}
                  <Text size="xs" c="dimmed">
                    вътр. {item.extension}
                  </Text>
                </Table.Td>
                <Table.Td>{item.groupName ?? '—'}</Table.Td>
                <Table.Td>{item.direction === 'IN' ? 'Входящо' : 'Изходящо'}</Table.Td>
                <Table.Td>{formatDuration(item.durationMs)}</Table.Td>
                <Table.Td>
                  <StatusBadge status={item.status} />
                </Table.Td>
                <Table.Td>
                  <ScoreText item={item} />
                </Table.Td>
                <Table.Td>
                  {item.isFlagged ? <Badge color="red">Проблемен</Badge> : null}
                </Table.Td>
              </Table.Tr>
            ))}

            {recordings.data?.items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={8}>
                  <Text ta="center" c="dimmed" py="lg">
                    Няма разговори за избраните филтри
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>

      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Общо {recordings.data?.total ?? 0} разговора
        </Text>
        <Pagination value={page} onChange={setPage} total={totalPages} />
      </Group>
    </Stack>
  );
}

function StatCard({
  label,
  value,
  hint,
  color,
}: {
  label: string;
  value: number | string;
  hint?: string;
  color?: string;
}) {
  return (
    <Paper withBorder p="md">
      <Text size="xs" c="dimmed" tt="uppercase">
        {label}
      </Text>
      <Text size="xl" fw={700} c={color}>
        {value}
      </Text>
      {hint && (
        <Text size="xs" c="dimmed">
          {hint}
        </Text>
      )}
    </Paper>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    QUEUED: 'gray',
    PROCESSING: 'blue',
    COMPLETED: 'green',
    FAILED: 'red',
  };

  return <Badge color={colors[status] ?? 'gray'}>{STATUS_LABELS[status] ?? status}</Badge>;
}

function ScoreText({ item }: { item: RecordingListItem }) {
  if (item.overallScore === null) return <Text c="dimmed">—</Text>;

  return (
    <Text fw={600} c={item.isFlagged ? 'red' : undefined}>
      {item.overallScore}
    </Text>
  );
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('bg-BG', {
    timeZone: 'Europe/Sofia',
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function formatDuration(ms: number | null): string {
  if (ms === null) return '—';

  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
